import { ArrowRight, Compass, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { photoBackground, RegulatedNote } from "../../components/Cards";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { useLibreWallet, useMyApartados, useMyRequests, useProducts, useReservationConfig } from "../../lib/queries";
import { APARTADO_STATUS_LABELS, REQUEST_STATUS_LABELS, REQUEST_STATUS_TONES, mxn } from "../../lib/reservations";
import { APARTADO_PITCH, apartadoPitchBody, perPassengerLabel } from "@aventuria/client-core/reservations";

const SHADE = "linear-gradient(180deg, rgba(0,37,87,0.1) 0%, rgba(0,37,87,0.85) 100%)";
const PITCH_SHADE = "linear-gradient(180deg, rgba(0,37,87,0) 45%, rgba(0,37,87,0.85) 100%)";

/**
 * The editorial card of the client's Apartados redesign (13 Oct 2026): photo, per-passenger minimum, three steps and
 * the way into the catalogue. It is the empty state; once the customer holds Apartados it shrinks to the pitch and the
 * call to action (no steps) below their list.
 */
function ApartadoPitchCard({ photoUrl, perPassenger, compact, onExplore }: { photoUrl: string | null | undefined; perPassenger: number; compact: boolean; onExplore: () => void }) {
  return (
    <section className={`apartado-pitch${compact ? " compact" : ""}`} aria-labelledby="apartado-pitch-title">
      <div className="apartado-pitch-photo" style={photoBackground(photoUrl, PITCH_SHADE, "half")}>
        <span className="apartado-pitch-eyebrow">{APARTADO_PITCH.eyebrow}</span>
        <span className="apartado-pitch-price"><small>Desde</small><strong translate="no">{perPassengerLabel(perPassenger)}</strong><small>MXN / pasajero</small></span>
      </div>
      <div className="apartado-pitch-body">
        <h2 id="apartado-pitch-title">{APARTADO_PITCH.title}</h2>
        <p>{apartadoPitchBody(perPassenger)}</p>
        {!compact && (
          <ol className="apartado-steps">
            {APARTADO_PITCH.steps.map((step, index) => (
              <li key={step.title}><span className="apartado-step-n" aria-hidden="true">{index + 1}</span><div><strong>{step.title}</strong><span>{step.body}</span></div></li>
            ))}
          </ol>
        )}
        <button type="button" className="primary-button solid apartado-pitch-cta" onClick={onExplore}>{APARTADO_PITCH.cta} <ArrowRight size={17} aria-hidden="true" /></button>
      </div>
    </section>
  );
}

/**
 * "Apartado" (formerly Wallet Aventuria) in the products hub is the trip wallet: every Apartado the customer holds
 * over their Wallet Libre, the money it blocks, and the open requests that could become one.
 */
export function ApartadosHubPage() {
  usePageHeader("Apartados", "Tus Apartados: dinero de tu Wallet Libre destinado a cada viaje.");
  const navigate = useNavigate();
  const apartados = useMyApartados();
  const requests = useMyRequests();
  const { wallet } = useLibreWallet();
  const products = useProducts();
  const config = useReservationConfig();

  if (apartados.isLoading || requests.isLoading) return <div className="page-state"><Spinner label="Cargando tus Apartados" /></div>;
  const list = apartados.data ?? [];
  const reserved = list.filter((apartado) => apartado.status === "active" || apartado.status === "funding_pending" || apartado.status === "completed").reduce((sum, apartado) => sum + apartado.balance, 0);
  const pending = (requests.data?.requests ?? []).filter((request) => request.statusGroup !== "closed" && !request.apartado);
  const allRequests = requests.data?.requests ?? [];
  const openCount = allRequests.filter((request) => request.statusGroup !== "closed").length;
  const perPassenger = config.data?.apartadoMinPerPassengerMxn ?? 9000;
  // The same photo as the "Apartado" product card on Inicio, so the two read as one product.
  const photoUrl = products.data?.products.find((product) => product.id === "travel")?.photoUrl;
  const pitch = (compact: boolean) => <ApartadoPitchCard photoUrl={photoUrl} perPassenger={perPassenger} compact={compact} onExplore={() => navigate("/explorar")} />;

  return (
    <div className="page-container">
      <section className="apartados-summary">
        <div>
          <span className="apartados-summary-label">Apartado para viajes</span>
          <strong translate="no">{mxn(reserved)}</strong>
          {wallet && <span className="request-meta">Disponible en Wallet Libre: <span translate="no">{mxn(wallet.balance.free)}</span></span>}
        </div>
        {allRequests.length > 0 && (
          // Every reservation request and proposal, the start of each Apartado, is one tap away here.
          <button type="button" className="apartados-requests-pill" onClick={() => navigate("/solicitudes")}>
            {openCount ? `Mis solicitudes · ${openCount}` : "Mis solicitudes"} <ArrowRight size={15} aria-hidden="true" />
          </button>
        )}
      </section>

      {list.length ? (
        <div className="apartado-grid">
          {list.map((apartado) => (
            <button type="button" key={apartado.id} className="apartado-card" onClick={() => navigate(`/apartados/${apartado.id}`)}>
              <div className="apartado-card-photo" style={photoBackground(apartado.request?.tripPhotoUrl, SHADE)}>
                <span className="glass-pill"><Lock size={12} aria-hidden="true" /> {APARTADO_STATUS_LABELS[apartado.status]}</span>
                <strong>{apartado.tripName}</strong>
              </div>
              <div className="apartado-card-body">
                <div className="label-row"><strong translate="no">{mxn(apartado.balance)}</strong><span className="request-meta" translate="no">meta {mxn(apartado.goalAmount)}</span></div>
                <div className="goal-bar"><span style={{ width: `${Math.round((apartado.progress ?? 0) * 100)}%` }} /></div>
                <span className="request-meta">{apartado.request ? `Solicitud ${apartado.request.folio} · ${REQUEST_STATUS_LABELS[apartado.request.status]}` : ""}</span>
              </div>
            </button>
          ))}
        </div>
      ) : pitch(false)}

      {pending.length > 0 && (
        <>
          <div className="section-heading">
            <div><span className="page-eyebrow">Solicitudes</span><h2>Viajes que puedes apartar</h2></div>
            <button type="button" className="link-button" onClick={() => navigate("/solicitudes")}>Mis solicitudes <ArrowRight size={14} aria-hidden="true" /></button>
          </div>
          <div className="next-trip-grid">
            {pending.map((request) => (
              <button key={request.id} type="button" className="next-trip-card" onClick={() => navigate(`/solicitudes/${request.id}`)}>
                <span className="benefit-icon"><Compass size={17} aria-hidden="true" /></span>
                <div><strong>{request.trip.name}</strong><span><span className={`status-pill ${REQUEST_STATUS_TONES[request.status]}`}>{REQUEST_STATUS_LABELS[request.status]}</span> <span translate="no">{request.folio}</span></span></div>
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            ))}
          </div>
        </>
      )}
      {list.length > 0 && pitch(true)}
      <RegulatedNote kind="wallet" />
    </div>
  );
}
